import React, { useRef } from "react";
import PropTypes from "prop-types";

let pinIconUid = 0;

const ALLOWED_IMAGE_HOST_SUFFIXES = [
  "insomniacookies.com",
  "localhost",
];

/**
 * Allow https/http CDN hosts for Insomnia product art, plus same-origin / relative paths.
 * Rejects javascript:, data:, and unknown hosts.
 */
export const getSafePinImageUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== "string") {
    return null;
  }
  const trimmed = imageUrl.trim();
  if (!trimmed) {
    return null;
  }

  // Relative / root-relative asset paths (incl. Jest fixtures like "cookie.png")
  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) {
    return trimmed;
  }
  if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed) && !trimmed.startsWith("//")) {
    return trimmed;
  }

  try {
    const base =
      typeof window !== "undefined" && window.location
        ? window.location.origin
        : "https://insomniacookies.com";
    const parsed = new URL(trimmed, base);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
      return null;
    }
    const host = parsed.hostname.toLowerCase();
    const allowed = ALLOWED_IMAGE_HOST_SUFFIXES.some(
      (suffix) => host === suffix || host.endsWith(`.${suffix}`),
    );
    return allowed ? trimmed : null;
  } catch (e) {
    return null;
  }
};

/**
 * Map-pin shaped product thumbnail for ATC toast.
 * tipUp (rotation 180): tip points toward cart; image centered in the pin head.
 */
export default function PinIcon({
  imageUrl,
  rotation = 0,
  width = 62,
  height = 76,
  circleColor = "#431b56",
  withShadow = false
}) {
  const uidRef = useRef(null);
  if (uidRef.current === null) {
    pinIconUid += 1;
    uidRef.current = String(pinIconUid);
  }
  const uid = uidRef.current;

  const tipUp = rotation === 180;
  const imageCenterY = tipUp ? 46 : 38;
  const imageSize = tipUp ? 54 : 45;
  const imageX = tipUp ? 31 - imageSize / 2 : 9;
  const imageY = tipUp ? imageCenterY - imageSize / 2 : 26;
  const clipId = tipUp
    ? `cookieToastPinClipTipUp-${uid}`
    : `cookieToastPinClip-${uid}`;
  const shadowFilterId = `cookieToastPinElevation3-${uid}`;
  const safeImageUrl = getSafePinImageUrl(imageUrl);

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 62 76"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      overflow="visible"
    >
      {withShadow && (
        <defs>
          <filter
            id={shadowFilterId}
            x="-40%"
            y="-25%"
            width="180%"
            height="160%"
            filterUnits="objectBoundingBox"
            colorInterpolationFilters="sRGB"
          >
            <feMorphology in="SourceAlpha" radius="3" operator="dilate" result="spread" />
            <feOffset dy="3" in="spread" result="offset" />
            <feGaussianBlur in="offset" stdDeviation="5" result="blur" />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.19 0"
              result="shadow"
            />
            <feBlend in="SourceGraphic" in2="shadow" mode="normal" />
          </filter>
        </defs>
      )}

      <g
        transform={`rotate(${rotation}, 31, 38)`}
        filter={withShadow ? `url(#${shadowFilterId})` : undefined}
      >
        <path
          d="
            M31 0
            C47 0 62 15 62 31
            C62 55 31 76 31 76
            C31 76 0 55 0 31
            C0 15 15 0 31 0
            Z
          "
          fill="white"
        />
        <circle cx="31" cy="30" r="27" fill={circleColor} />
      </g>

      <clipPath id={clipId}>
        <circle cx="31" cy={imageCenterY} r="27" />
      </clipPath>

      {safeImageUrl && (
        <image
          href={safeImageUrl}
          x={imageX}
          y={imageY}
          width={imageSize}
          height={imageSize}
          clipPath={`url(#${clipId})`}
          preserveAspectRatio="xMidYMid slice"
        />
      )}
    </svg>
  );
}

PinIcon.propTypes = {
  rotation: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  imageUrl: PropTypes.string,
  circleColor: PropTypes.string,
  withShadow: PropTypes.bool
};

PinIcon.defaultProps = {
  rotation: 0,
  width: 62,
  height: 76,
  imageUrl: "",
  circleColor: "#431b56",
  withShadow: false
};
